import type { PrimaryPageAction } from "@octopusdeploy/design-system-components";
import { VariableType } from "@octopusdeploy/octopus-server-client";
import { isEqual } from "lodash";
import type { VariableModel, VariableValueModel } from "~/areas/variables/VariablesModel";
import type { PrimarySaveActionOptions, PrimarySavePageAction } from "~/components/FormPaperLayout/LegacyForm";

export function getProjectVariablesPrimaryAction(
    sensitiveChanged: boolean,
    textChanged: boolean,
    createSaveAction: (options: PrimarySaveActionOptions) => PrimarySavePageAction,
    getCommitButtonPrimaryAction: (defaultPrimaryAction: PrimarySavePageAction, sensitiveChanged: boolean) => PrimaryPageAction
): PrimaryPageAction {
    if (sensitiveChanged && textChanged) {
        const saveAction = createSaveAction({ saveButtonLabel: "Save & Commit", saveButtonBusyLabel: "Saving & Committing" });

        return getCommitButtonPrimaryAction(saveAction, sensitiveChanged);
    }

    if (sensitiveChanged) {
        const saveAction = createSaveAction({ saveButtonLabel: "Save", saveButtonBusyLabel: "Saving" });

        return { extraContext: "Sensitive variables have changed. The changes will be written to the database and apply to all branches.", ...saveAction };
    }

    const saveAction = createSaveAction({ saveButtonLabel: "Commit", saveButtonBusyLabel: "Committing" });
    saveAction.disabled = !textChanged; // default to commit button, disabled if there are no changes

    return getCommitButtonPrimaryAction(saveAction, sensitiveChanged);
}

export function getVariableTypesChanged(variableModels: ReadonlyArray<VariableModel>, initialVariables: ReadonlyArray<VariableModel>) {
    const cleanVariablesMap = createVariableValueHashtable(initialVariables);
    const dirtyVariablesMap = createVariableValueHashtable(variableModels);

    let sensitiveChanged = false;
    let textChanged = false;

    for (const key in cleanVariablesMap) {
        const cleanVariable = cleanVariablesMap[key];
        const dirtyVariable = dirtyVariablesMap[key];

        if (!dirtyVariable) {
            if (cleanVariable.Type === VariableType.Sensitive) {
                sensitiveChanged = true;
            } else {
                textChanged = true;
            }

            // variable has been changed
        } else if (!isEqual(cleanVariable, dirtyVariable)) {
            // changed the variable sensitive to non-sensitive or vice versa
            // we need to save both as it would be removing from one and adding to another
            const cleanVariableIsSensitive = cleanVariable.Type === VariableType.Sensitive;
            const dirtyVariableIsSensitive = dirtyVariable.Type === VariableType.Sensitive;

            if (cleanVariableIsSensitive != dirtyVariableIsSensitive) {
                sensitiveChanged = true;
                textChanged = true;
            } else if (dirtyVariableIsSensitive) {
                sensitiveChanged = true;
            } else {
                textChanged = true;
            }

            if (sensitiveChanged && textChanged) {
                return { sensitiveChanged, textChanged };
            }
        }
    }

    for (const key in dirtyVariablesMap) {
        const cleanVariable = cleanVariablesMap[key];
        const dirtyVariable = dirtyVariablesMap[key];

        if (!cleanVariable) {
            if (dirtyVariable.Type === VariableType.Sensitive) {
                sensitiveChanged = true;
            } else {
                textChanged = true;
            }

            if (sensitiveChanged && textChanged) {
                return { sensitiveChanged, textChanged };
            }
        }
    }

    return { sensitiveChanged, textChanged };
}

function createVariableValueHashtable(variables: ReadonlyArray<VariableModel>) {
    const hashtable: Record<string, flattenedVariableValue> = {};
    variables.forEach((variable) => {
        variable.values.forEach((value) => {
            hashtable[value.Id] = { name: variable.name, ...value };
        });
    });
    return hashtable;
}

interface flattenedVariableValue extends VariableValueModel {
    name: string;
}
